import { useEffect, useState } from 'react'
import { Handshake, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/utils/cn'
import { formatPhone } from '@/utils/formatPhone'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { useAuthStore } from '@/stores/authStore'
import { canWriteParceiros } from '@/utils/permissions'
import type { CloserLeadFormData, PartnerCommissionMode } from '@/types'

type PartnerOption = {
  id: string
  nome: string
  empresa?: string | null
  modoComissao?: PartnerCommissionMode
  indicacaoAtiva?: boolean
}

type QuickPartnerForm = {
  nome: string
  email: string
  telefone: string
  comissao: string
  modoComissao: PartnerCommissionMode
  indicadoPorId: string
}

const EMPTY_QUICK_FORM: QuickPartnerForm = {
  nome: '',
  email: '',
  telefone: '',
  comissao: '10',
  modoComissao: 'RECORRENTE',
  indicadoPorId: '',
}

const SELECT_CLASS =
  'w-full appearance-none rounded-md border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 pr-9 text-[13px] text-[var(--text-primary)] outline-none transition-colors focus:border-[var(--brand-primary)]'

interface LeadPartnerFieldProps {
  formData: CloserLeadFormData
  onMultiFieldChange: (fields: Partial<CloserLeadFormData>) => void
  errors?: Record<string, string>
}

export function LeadPartnerField({ formData, onMultiFieldChange, errors }: LeadPartnerFieldProps) {
  const userRole = useAuthStore((s) => s.user?.role)
  const canCreatePartner = canWriteParceiros(userRole)
  const [partners, setPartners] = useState<PartnerOption[]>([])
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [quick, setQuick] = useState<QuickPartnerForm>(EMPTY_QUICK_FORM)

  useEffect(() => {
    const controller = new AbortController()
    api
      .get('/parceiros/options', { signal: controller.signal })
      .then((resp) => setPartners(resp.data?.data ?? []))
      .catch(() => {
        if (!controller.signal.aborted) setPartners([])
      })
    return () => controller.abort()
  }, [])

  const answer = formData.temParceiro ?? (formData.parceiroId ? 'sim' : undefined)
  const error = errors?.temParceiro || errors?.parceiroId
  const selected = partners.find((p) => p.id === formData.parceiroId)
  const activePartners = partners.filter((p) => p.indicacaoAtiva !== false || p.id === formData.parceiroId)

  const choose = (value: 'sim' | 'nao') => {
    onMultiFieldChange(value === 'nao' ? { temParceiro: 'nao', parceiroId: null } : { temParceiro: 'sim' })
    if (value === 'nao') setCreating(false)
  }

  const createPartner = async () => {
    const comissao = Number(quick.comissao.replace(',', '.'))
    if (quick.nome.trim().length < 2 || !quick.email.trim() || quick.telefone.replace(/\D/g, '').length < 8) {
      toast.error('Informe nome, e-mail e telefone do parceiro')
      return
    }
    if (!Number.isFinite(comissao) || comissao < 0) {
      toast.error('Comissão inválida')
      return
    }
    setSaving(true)
    try {
      const resp = await api.post('/parceiros', {
        nome: quick.nome.trim(),
        email: quick.email.trim(),
        telefone: quick.telefone,
        comissao,
        modoComissao: quick.modoComissao,
        indicadoPorId: quick.indicadoPorId || null,
      })
      const created = resp.data?.data as PartnerOption
      setPartners((prev) => [...prev, created].sort((a, b) => a.nome.localeCompare(b.nome)))
      onMultiFieldChange({ temParceiro: 'sim', parceiroId: created.id })
      setCreating(false)
      setQuick(EMPTY_QUICK_FORM)
      toast.success(`Parceiro ${created.nome} cadastrado e vinculado`)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao cadastrar parceiro'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        <div className='rounded-lg bg-accent/10 p-1.5 text-accent'>
          <Handshake size={16} />
        </div>
        <span className='text-xs font-bold uppercase tracking-[0.15em] text-text-muted'>Parceiro *</span>
      </div>

      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2' role='radiogroup' aria-label='O lead veio de parceiro?'>
        {([
          { id: 'nao', label: 'Não, indicação direta' },
          { id: 'sim', label: 'Sim, veio de parceiro' },
        ] as const).map((option) => (
          <button
            key={option.id}
            type='button'
            role='radio'
            aria-checked={answer === option.id}
            onClick={() => choose(option.id)}
            className={cn(
              'rounded-lg border px-3 py-2 text-left text-[13px] font-medium transition-colors',
              answer === option.id
                ? 'border-accent bg-accent/10 text-[var(--text-primary)]'
                : 'border-[var(--glass-border)] text-[var(--text-muted)] hover:border-accent/40',
              error && !answer && 'border-[#e5484d]/60',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {answer === 'sim' && (
        <div className='space-y-2'>
          <div className='relative'>
            <select
              aria-label='Parceiro'
              value={formData.parceiroId || ''}
              onChange={(e) => onMultiFieldChange({ temParceiro: 'sim', parceiroId: e.target.value || null })}
              className={cn(SELECT_CLASS, errors?.parceiroId && 'border-[#e5484d]/60')}
            >
              <option value=''>Selecione o parceiro...</option>
              {activePartners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                  {p.empresa ? ` — ${p.empresa}` : ''}
                </option>
              ))}
            </select>
            <Handshake size={14} className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-muted' />
          </div>
          {selected && (
            <p className='text-[11px] text-emerald-600 dark:text-emerald-400'>Lead vinculado a {selected.nome}.</p>
          )}

          {canCreatePartner && !creating && (
            <button
              type='button'
              onClick={() => setCreating(true)}
              className='flex items-center gap-1 text-[12px] font-medium text-accent hover:underline'
            >
              <Plus size={13} /> Cadastrar novo parceiro
            </button>
          )}
          {!canCreatePartner && (
            <p className='text-[11px] text-[var(--text-muted)]'>
              Parceiro não está na lista? Peça a um closer ou gestor para cadastrá-lo.
            </p>
          )}

          {creating && (
            <div className='space-y-3 rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-3'>
              <div className='flex items-center justify-between'>
                <p className='text-[12px] font-semibold text-[var(--text-primary)]'>Novo parceiro</p>
                <button
                  type='button'
                  onClick={() => setCreating(false)}
                  className='rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                  aria-label='Cancelar cadastro de parceiro'
                >
                  <X size={14} />
                </button>
              </div>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <Input label='Nome *' value={quick.nome} onChange={(e) => setQuick({ ...quick, nome: e.target.value })} />
                <Input
                  label='E-mail *'
                  type='email'
                  value={quick.email}
                  onChange={(e) => setQuick({ ...quick, email: e.target.value })}
                />
                <Input
                  label='Telefone *'
                  value={quick.telefone}
                  onChange={(e) => setQuick({ ...quick, telefone: formatPhone(e.target.value) })}
                  placeholder='(00) 00000-0000'
                />
                <Input
                  label='Comissão (%)'
                  inputMode='decimal'
                  value={quick.comissao}
                  onChange={(e) => setQuick({ ...quick, comissao: e.target.value })}
                />
                <div className='flex flex-col gap-1.5'>
                  <label className='font-medium uppercase tracking-wider text-[var(--font-color-label)]' style={{ fontSize: 'var(--font-size-label)' }}>
                    Regra do parceiro externo
                  </label>
                  <select
                    value={quick.modoComissao}
                    onChange={(e) => {
                      const modoComissao = e.target.value as PartnerCommissionMode
                      setQuick({
                        ...quick,
                        modoComissao,
                        ...(modoComissao === 'DUAS_MENSALIDADES' ? { comissao: '100' } : {}),
                      })
                    }}
                    className={SELECT_CLASS}
                  >
                    <option value='RECORRENTE'>Percentual recorrente</option>
                    <option value='DUAS_MENSALIDADES'>Indicador: 100% da 1ª parcela</option>
                  </select>
                </div>
                <div className='flex flex-col gap-1.5'>
                  <label className='font-medium uppercase tracking-wider text-[var(--font-color-label)]' style={{ fontSize: 'var(--font-size-label)' }}>
                    Indicado por
                  </label>
                  <select
                    value={quick.indicadoPorId}
                    onChange={(e) => setQuick({ ...quick, indicadoPorId: e.target.value })}
                    className={SELECT_CLASS}
                  >
                    <option value=''>Ninguém (topo da pirâmide)</option>
                    {activePartners.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='flex justify-end'>
                <Button type='button' variant='primary' size='sm' loading={saving} onClick={createPartner}>
                  Cadastrar e vincular
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className='text-[#e5484d]' style={{ fontSize: 'var(--font-size-xs)' }}>
          {error}
        </p>
      )}
    </div>
  )
}

export default LeadPartnerField
